import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography, Chip, LinearProgress, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useAppTheme } from '../../hooks/useAppTheme';
import { ScoreComponent, SignalData } from '../../store/optionsSlice';

interface Props {
  signal?: SignalData | null;
  defaultExpanded?: boolean;
}

/**
 * "Why this signal?" — collapsed-by-default explainer surfacing the top 3
 * contributing factors to the FNO score, with their weight and individual
 * sub-score. Pulled straight from `signal.triggerConditions.components` —
 * no extra fetch needed.
 */
const WhyThisSignalPanel: React.FC<Props> = ({ signal, defaultExpanded = false }) => {
  const { C } = useAppTheme();
  if (!signal) return null;
  const components: ScoreComponent[] = signal.triggerConditions?.components || [];
  if (components.length === 0) return null;

  // Sort by contribution = weight × score
  const contribution = (c: ScoreComponent) => Number(c.weight || 0) * Number(c.score || 0);
  const top3 = [...components]
    .sort((a, b) => contribution(b) - contribution(a))
    .slice(0, 3);

  const totalContribution = components.reduce((sum, c) => sum + contribution(c), 0) || 1;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        bgcolor: C.neutralMuted,
        border: '1px solid',
        borderColor: C.neutralBorder,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LightbulbIcon sx={{ fontSize: 18, color: C.warn }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.neutral }}>
            Why this signal?
          </Typography>
          <Chip
            size="small"
            label={`Score ${Number(signal.score).toFixed(0)}`}
            sx={{ ml: 0.5, height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: 'background.paper' }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          Top 3 contributing factors (weight × sub-score), in order of impact on the final score.
        </Typography>
        <Stack spacing={1}>
          {top3.map((c, i) => {
            const contrib = contribution(c);
            const contribPct = (contrib / totalContribution) * 100;
            const tone = c.pass ? C.bull : C.bear;
            return (
              <Box key={`${c.name}-${i}`} sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{
                      width: 18, height: 18, borderRadius: '50%',
                      bgcolor: tone, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.62rem', fontWeight: 800,
                    }}>
                      {i + 1}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.name}</Typography>
                    <Chip
                      size="small"
                      label={c.pass ? 'pass' : 'fail'}
                      sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700,
                        bgcolor: c.pass ? C.bullMuted : C.bearMuted,
                        color: c.pass ? C.bull : C.bear }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number(c.score).toFixed(0)} × {Number(c.weight).toFixed(2)}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${contribPct.toFixed(0)}%`}
                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: tone, color: '#fff' }}
                    />
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, Number(c.score)))}
                  sx={{
                    height: 4, borderRadius: 2,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': { bgcolor: tone, borderRadius: 2 },
                  }}
                />
                {c.reason && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                    {c.reason}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
        {components.length > 3 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
            +{components.length - 3} more factors — full breakdown on the Signal Card.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default WhyThisSignalPanel;
